import { useMemo, useState, type ReactElement } from 'react';
import { Boxes, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';
import { toast } from '@/hooks/useToast';

import {
  useBaseVersions,
  useCreateTenant,
  useDmAvailable,
  useReinitTenant,
  useSeedTenant,
  useTenants,
  useUpdateTenant,
  type CreateTenantInput,
  type TenantViewModel,
} from '@/features/platform/api';
import { CreateTenantDialog } from '@/features/platform/components/CreateTenantDialog';
import { EditTenantDialog } from '@/features/platform/components/EditTenantDialog';
import { JobProgressIndicator } from '@/features/platform/components/JobProgressIndicator';
import { SeedConfirmDialog } from '@/features/platform/components/SeedConfirmDialog';
import { DataTable } from '@/components/ui/data-table';
import { TenantsTable } from '@/features/platform/components/TenantsTable';
import {
  isSeedAvailable,
  isSeedDisabledGate404,
  markSeedUnavailable,
} from '@/features/platform/seedAvailability';

export function PlatformTenantsPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tenantsQuery = useTenants();
  const { available: dmAvailable } = useDmAvailable();
  // Base image tags for the create dialog's override pre-fill (Phase 4). Gated
  // on DM availability so it never fires in production; any error resolves an
  // all-null map (no pre-fill) rather than surfacing an error.
  const baseVersionsQuery = useBaseVersions(dmAvailable);
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant();
  const reinitTenant = useReinitTenant();
  const seedTenant = useSeedTenant();

  const [createOpen, setCreateOpen] = useState(false);
  // Snapshot DM reachability at the moment the create dialog opens so a mid-
  // session DM flap can't silently strip an operator's typed overrides. (CR4)
  const [createDmSnapshot, setCreateDmSnapshot] = useState(false);
  const [editing, setEditing] = useState<TenantViewModel | null>(null);
  const [seeding, setSeeding] = useState<TenantViewModel | null>(null);
  const [reinitJobId, setReinitJobId] = useState<number | null>(null);
  // Local mirror of the module-level flag so a 404 re-renders the table.
  const [seedEnabled, setSeedEnabled] = useState(isSeedAvailable());

  const tenants = useMemo(
    () =>
      tenantsQuery.isSuccess
        ? [...tenantsQuery.data.tenants].sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [tenantsQuery.isSuccess, tenantsQuery.data],
  );

  function handleCreate(input: CreateTenantInput): void {
    createTenant.mutate(input, {
      onSuccess: (result) => {
        setCreateOpen(false);
        createTenant.reset();
        // Create-with-overrides launches a deploy job (202). Subscribe to it so
        // the operator sees progress; the tenant-list polling (driven by
        // `deployment_status`) keeps the row badge live until terminal.
        if (result.launchedJobExecutionId !== null) {
          setReinitJobId(result.launchedJobExecutionId);
          toast({
            title: t('routes.platform.tenants.create.launched', {
              id: result.launchedJobExecutionId,
            }),
            variant: 'success',
          });
        } else if (result.overridesRequestedButNotDeployed) {
          // 201 with override rows saved but no deploy job — DM was unreachable
          // server-side. Say so explicitly instead of implying a deploy. (CR5)
          toast({
            title: t('routes.platform.tenants.create.overridesNotDeployed'),
            variant: 'destructive',
          });
        }
      },
    });
  }

  function handleEdit(input: Parameters<typeof updateTenant.mutate>[0]): void {
    updateTenant.mutate(input, {
      onSuccess: () => {
        setEditing(null);
        updateTenant.reset();
      },
    });
  }

  function handleReinit(tenant: TenantViewModel): void {
    reinitTenant.mutate(tenant.uuid, {
      onSuccess: (launch) => {
        // `jobId` is a string on the wire; the GET /jobs/{id} path expects a Long.
        setReinitJobId(Number(launch.jobId));
        toast({
          title: t('routes.platform.tenants.reinit.launched', { id: launch.jobId }),
          variant: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: t('routes.platform.tenants.reinit.error', { code: getErrorCodeForUi(error) }),
          variant: 'destructive',
        });
      },
    });
  }

  function handleSeed(tenant: TenantViewModel): void {
    seedTenant.mutate(tenant.uuid, {
      onSuccess: () => {
        setSeeding(null);
        seedTenant.reset();
        toast({ title: t('routes.platform.tenants.seed.launched'), variant: 'success' });
      },
      onError: (error) => {
        if (isSeedDisabledGate404(error)) {
          // Empty-body 404 = the env has the seed endpoint disabled. Latch the
          // global flag and hide the action thereafter.
          markSeedUnavailable();
          setSeedEnabled(false);
          setSeeding(null);
          seedTenant.reset();
          toast({ title: t('routes.platform.tenants.seed.unavailable'), variant: 'destructive' });
          return;
        }
        // Any other error (incl. an enveloped tenant-not-found 404) is a
        // per-call failure — toast it, do NOT latch the global flag.
        toast({
          title: t('routes.platform.tenants.seed.error', { code: getErrorCodeForUi(error) }),
          variant: 'destructive',
        });
      },
    });
  }

  async function handleCopyUuid(tenant: TenantViewModel): Promise<void> {
    try {
      await navigator.clipboard.writeText(tenant.uuid);
      toast({ title: t('routes.platform.tenants.uuidCopied') });
    } catch {
      // Clipboard can be unavailable (insecure context); silently ignore.
    }
  }

  return (
    <>
      <PageHeader
        icon={<Boxes className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.platform.tenants.heading')}
        subtitle={t('routes.platform.tenants.subheading')}
        actions={
          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setCreateDmSnapshot(dmAvailable);
                setCreateOpen(true);
              }}
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('routes.platform.tenants.createAction')}
            </Button>
          </div>
        }
      />
      <div className="space-y-4 bg-background px-8 py-5">
        {reinitJobId !== null && <JobProgressIndicator jobExecutionId={reinitJobId} />}

        {tenantsQuery.isPending && <TenantsListSkeleton />}

        {tenantsQuery.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={tenantsQuery.error}
              heading={t('routes.platform.tenants.error.heading')}
              message={t('routes.platform.tenants.error.message')}
              codeLabel={t('routes.platform.tenants.error.codeLabel')}
              onRetry={() => {
                tenantsQuery.refetch().catch(() => undefined);
              }}
              retryLabel={t('routes.platform.tenants.error.retry')}
            />
          </div>
        )}

        {tenantsQuery.isSuccess && tenants.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {t('routes.platform.tenants.empty')}
          </p>
        )}

        {tenantsQuery.isSuccess && tenants.length > 0 && (
          <DataTable aria-label={t('routes.platform.tenants.listAriaLabel')}>
            <TenantsTable
              tenants={tenants}
              seedEnabled={seedEnabled}
              showOverrides={dmAvailable}
              onView={(tenant) => {
                void navigate(`/platform/tenants/${tenant.uuid}`);
              }}
              onEdit={(tenant) => { setEditing(tenant); }}
              onReinit={handleReinit}
              onSeed={(tenant) => { setSeeding(tenant); }}
              onCopyUuid={(tenant) => {
                void handleCopyUuid(tenant);
              }}
            />
          </DataTable>
        )}
      </div>

      <CreateTenantDialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) createTenant.reset();
        }}
        onSubmit={handleCreate}
        pending={createTenant.isPending}
        submitError={createTenant.error}
        dmAvailable={createDmSnapshot}
        baseVersions={createDmSnapshot ? (baseVersionsQuery.data ?? null) : null}
      />

      <EditTenantDialog
        tenant={editing}
        onCancel={() => {
          setEditing(null);
          updateTenant.reset();
        }}
        onSubmit={handleEdit}
        pending={updateTenant.isPending}
        submitError={updateTenant.error}
      />

      <SeedConfirmDialog
        tenant={seeding}
        onCancel={() => {
          setSeeding(null);
          seedTenant.reset();
        }}
        onConfirm={handleSeed}
        pending={seedTenant.isPending}
        submitError={seedTenant.error}
      />
    </>
  );
}

function TenantsListSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-md" />
      ))}
    </div>
  );
}
